/**
 * Investor-Portal — Odoo-Hygiene-Audit (#1182 Phase 2, v2.567.0)
 *
 * Lebendes Werkzeug: 1 Master-Ampel (Check 1) oben + 6 Check-Cards
 * (sortiert nach €-Wirkung), je mit Ampel / Treffer / Σ€ / aufklappbarer Mängelliste.
 *
 * Hot-Spot-Finder, keine Null-Formel. Differenz wird ehrlich gelabelt:
 * „Master-Ampel = Trend-Indikator, NICHT muss-auf-0."
 *
 * Read-only — schreibt NIE nach Odoo. Reload zeigt nach Odoo-Fix Mängel-Reduktion.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, Cell,
} from 'recharts';
import {
  RefreshCw, AlertCircle, ChevronDown, ChevronRight, Info,
  CheckCircle2, AlertTriangle, XCircle, Settings,
} from 'lucide-react';
import PeriodToggle, { buildPeriodQuery } from './components/PeriodToggle';
import DataMaturityBanner from './components/DataMaturityBanner';

const API = import.meta.env.VITE_API_URL || '';
const MONTH_LABELS = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

function fmtEur(v) { if (v==null||isNaN(v)) return '–'; return new Intl.NumberFormat('de-AT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(v); }
function fmtEurDetail(v) { if (v==null||isNaN(v)) return '–'; return new Intl.NumberFormat('de-AT',{style:'currency',currency:'EUR',maximumFractionDigits:2}).format(v); }
function fmtPct(v) { if (v==null||isNaN(v)) return '–'; return `${v.toFixed(1)} %`; }
function fmtInt(v) { if (v==null) return '–'; return new Intl.NumberFormat('de-AT').format(v); }

const AMPEL_COLORS = {
  green: { bg: '#D1FAE5', border: '#10B981', text: '#065F46', icon: CheckCircle2 },
  amber: { bg: '#FEF3C7', border: '#F59E0B', text: '#92400E', icon: AlertTriangle },
  red:   { bg: '#FEE2E2', border: '#EF4444', text: '#991B1B', icon: XCircle },
};

function AmpelBadge({ ampel, label, small }) {
  const c = AMPEL_COLORS[ampel] || AMPEL_COLORS.amber;
  const Icon = c.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md font-medium ${small ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'}`}
      style={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}` }}
    >
      <Icon width={small ? 11 : 13} height={small ? 11 : 13} />
      {label || (ampel === 'green' ? 'sauber' : ampel === 'amber' ? 'beobachten' : 'Mängel')}
    </span>
  );
}

export default function InvestorAudit() {
  const [searchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();

  const [periodState, setPeriodState] = useState(() => ({
    period: searchParams.get('period') || 'ytd',
    year: parseInt(searchParams.get('year') || currentYear, 10),
    month: parseInt(searchParams.get('month') || new Date().getMonth() + 1, 10),
    quarter: parseInt(searchParams.get('quarter') || Math.ceil((new Date().getMonth() + 1) / 3), 10),
    fromDate: searchParams.get('from_date') || null,
    toDate: searchParams.get('to_date') || null,
  }));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [check1Mode, setCheck1Mode] = useState('ytd'); // 'ytd' | 'monthly'
  const [showByDesign, setShowByDesign] = useState(false);

  const periodQuery = useMemo(() => buildPeriodQuery(periodState), [periodState]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const q = new URLSearchParams(periodQuery);
        const res = await fetch(`${API}/api/investor/audit?${q.toString()}`, { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) { setError(json.error || `HTTP ${res.status}`); setData(null); }
        else setData(json);
      } catch (e) { if (!abort) setError(e.message); }
      finally { if (!abort) setLoading(false); }
    }
    load();
    return () => { abort = true; };
  }, [periodQuery, reloadCounter]);

  const c = data?.checks;
  const masterAmpel = data?.master_ampel || 'green';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A2E]">Buchhaltungs-Audit</h1>
          <div className="text-sm text-[#6B7280] mt-1">
            Live-Mängelliste gegen Odoo: 6 Hot-Spot-Checks + 1 Master-Ampel. Read-only —
            Korrekturen erfolgen direkt in Odoo, Reload zeigt schrumpfende Mängel.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setReloadCounter(c => c + 1)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#E5E7EB] rounded-md bg-white text-[#4B5563] hover:bg-[#F8F9FC] disabled:opacity-60"
          >
            <RefreshCw width={13} height={13} className={loading ? 'animate-spin' : ''} />
            Neu laden
          </button>
          <PeriodToggle
            period={periodState.period} year={periodState.year} month={periodState.month}
            quarter={periodState.quarter} fromDate={periodState.fromDate} toDate={periodState.toDate}
            onChange={setPeriodState}
          />
        </div>
      </div>

      {/* #1184 v2.569.0 — Datenreife-Banner. Audit zeigt bewusst ALLE Monate
          (Hot-Spot-Finder), kein YTD-Cutoff — der Banner warnt nur kontextuell. */}
      <DataMaturityBanner periodState={periodState} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg flex items-center gap-2">
          <AlertCircle width={14} height={14} /> Fehler: {error}
        </div>
      )}

      {loading && !data && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center text-sm text-[#9CA3AF]">
          Lade Odoo-Daten… (~10 s — direkter Live-Read aus account.move.line + analytic.line)
        </div>
      )}

      {data && (
        <>
          {/* Master-Ampel (Check 1) */}
          <MasterCard
            check1={c.check1}
            mode={check1Mode}
            onModeChange={setCheck1Mode}
            masterAmpel={masterAmpel}
            universe={data.universe}
            period={data.period}
          />

          {/* Hinweis-Banner ehrliche Einordnung */}
          <div className="bg-[#F8F9FC] border border-[#E5E7EB] text-[#4B5563] text-xs px-3 py-2 rounded-lg flex items-start gap-2">
            <Info width={14} height={14} className="mt-0.5 shrink-0 text-[#338edc]" />
            <div>
              <span className="font-medium text-[#1A1A2E]">Hot-Spot-Finder, keine Null-Formel.</span>{' '}
              Die Differenz wird NICHT exakt durch die 6 Cards erklärt — Periodisierung
              und nicht-KS-pflichtige Konten bleiben außerhalb. Ziel ist Trend-Reduktion,
              nicht „exakt 0".
            </div>
          </div>

          {/* 6 Check-Cards, sortiert nach €-Wirkung */}
          <Check5Card check={c.check5} />
          <Check3Card check={c.check3} />
          <Check2Card check={c.check2} showByDesign={showByDesign} onToggle={setShowByDesign} />
          <Check4Card check={c.check4} />
          <Check7Card check={c.check7} />
          <Check6Card check={c.check6} />

          {/* Footer-Details */}
          <details className="bg-white border border-[#E5E7EB] rounded-xl text-xs">
            <summary className="cursor-pointer px-4 py-2 text-[#4B5563] hover:bg-[#F8F9FC] rounded-xl flex items-center gap-2">
              <Settings width={12} height={12} /> Audit-Konfiguration & Reconciliation
            </summary>
            <div className="px-4 py-3 border-t border-[#E5E7EB] grid grid-cols-1 md:grid-cols-2 gap-4 text-[#6B7280]">
              <div>
                <div className="font-medium text-[#1A1A2E] mb-1">Reconciliation-Rechnung</div>
                <div className="space-y-1 tabular-nums">
                  <div>Check 1 Diff: <span className="text-[#1A1A2E]">{fmtEurDetail(data.reconciliation.check1_diff)}</span></div>
                  <div>Check 2 (echte Fehler): <span className="text-[#1A1A2E]">{fmtEurDetail(data.reconciliation.check2_real_sum)}</span></div>
                  <div>Check 2 (Odoo-by-design): <span className="text-[#1A1A2E]">{fmtEurDetail(data.reconciliation.check2_by_design_sum)}</span></div>
                  <div>Check 5 (KS-los): <span className="text-[#1A1A2E]">{fmtEurDetail(data.reconciliation.check5_sum)}</span></div>
                  <div>Check 7 (Durchläufer): <span className="text-[#1A1A2E]">{fmtEurDetail(data.reconciliation.check7_sum)}</span></div>
                  <div className="pt-1 border-t border-[#E5E7EB] mt-1">
                    Unerklärter Rest: <span className="text-[#1A1A2E]">{fmtEurDetail(data.reconciliation.unexplained)}</span>
                  </div>
                </div>
              </div>
              <div>
                <div className="font-medium text-[#1A1A2E] mb-1">Konfiguration (system_config)</div>
                <div className="space-y-1">
                  <div>audit_durchlaeufer_toleranz: <code className="text-[#1A1A2E]">±{fmtEur(data.settings.audit_durchlaeufer_toleranz)}</code></div>
                  <div>audit_cross_ks_whitelist: <code className="text-[#1A1A2E]">{data.settings.audit_cross_ks_whitelist.length} Paare</code></div>
                  {data.settings.audit_cross_ks_whitelist.map((w, i) => (
                    <div key={i} className="pl-3 text-[#9CA3AF]">• {w.label || `KS #${w.primary_id} → #${w.cross_id}`}</div>
                  ))}
                </div>
                <div className="mt-3 font-medium text-[#1A1A2E] mb-1">Universe (geladen aus Odoo)</div>
                <div className="space-y-1">
                  <div>analytic.line: <span className="text-[#1A1A2E]">{fmtInt(data.universe.analytic_lines)}</span></div>
                  <div>move.line: <span className="text-[#1A1A2E]">{fmtInt(data.universe.move_lines)}</span></div>
                  <div>Compute-Zeit: <span className="text-[#1A1A2E]">{(data.elapsed_ms/1000).toFixed(2)} s</span></div>
                </div>
              </div>
            </div>
          </details>
        </>
      )}
    </div>
  );
}

// ─── Master-Card (Check 1) ────────────────────────────────────────────────────
function MasterCard({ check1, mode, onModeChange, masterAmpel, universe, period }) {
  const c = AMPEL_COLORS[check1.ampel];
  const monatsBars = useMemo(() => (check1.monats_trend || []).map(m => ({
    monat: MONTH_LABELS[m.monat - 1] || `M${m.monat}`,
    diff: m.diff != null ? Math.round(m.diff) : 0,
    analytic: Math.round(m.analytic),
    guv: m.guv != null ? Math.round(m.guv) : null,
  })), [check1]);
  return (
    <div
      className="rounded-xl border-2 overflow-hidden"
      style={{ backgroundColor: '#FFFFFF', borderColor: c.border }}
    >
      <div className="px-5 py-4 flex items-start justify-between gap-3 flex-wrap" style={{ backgroundColor: c.bg }}>
        <div className="flex items-start gap-3">
          <c.icon width={28} height={28} style={{ color: c.text }} />
          <div>
            <div className="text-xs uppercase tracking-wider font-medium" style={{ color: c.text }}>
              Master-Ampel — Check 1: GuV-Prüfsumme
            </div>
            <div className="text-2xl font-bold tabular-nums mt-0.5" style={{ color: c.text }}>
              Diff {fmtEurDetail(check1.diff)}
            </div>
            <div className="text-xs mt-1 max-w-2xl" style={{ color: c.text }} title={check1.hinweis}>
              {check1.hinweis}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-[10px] p-[3px] border border-[#E5E7EB] inline-flex">
          {[
            { id: 'ytd', label: 'Periode gesamt' },
            { id: 'monthly', label: 'Monatlich' },
          ].map(t => {
            const active = mode === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onModeChange(t.id)}
                className={`px-2.5 py-1 rounded-[7px] text-xs font-medium ${active ? 'bg-[#338edc] text-white' : 'text-[#6B7280] hover:text-[#1A1A2E]'}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">Σ analytic</div>
          <div className="text-xl font-semibold text-[#1A1A2E] tabular-nums mt-0.5">{fmtEurDetail(check1.sigma_analytic)}</div>
          <div className="text-[11px] text-[#9CA3AF] mt-0.5">{fmtInt(universe.analytic_lines)} Buchungen · {check1.analytic_lines} berücksichtigt</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">Σ GuV-EBITDA (UGB-Pivot)</div>
          <div className="text-xl font-semibold text-[#1A1A2E] tabular-nums mt-0.5">{fmtEurDetail(check1.sigma_guv)}</div>
          <div className="text-[11px] text-[#9CA3AF] mt-0.5">{check1.guv_monate} Monate · investor_ist_values</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">Worst-Ampel (alle 7 Checks)</div>
          <div className="mt-1"><AmpelBadge ampel={masterAmpel} label={
            masterAmpel === 'green' ? 'Alle Checks sauber' :
            masterAmpel === 'amber' ? 'Hinweise vorhanden' :
            'Mängel vorhanden'
          } /></div>
          <div className="text-[11px] text-[#9CA3AF] mt-1.5">Zeitraum {period.dateFrom} – {period.dateTo}</div>
        </div>
      </div>

      {/* Monats-Sparkline */}
      {mode === 'monthly' && monatsBars.length > 0 && (
        <div className="px-5 pb-4 pt-1">
          <div className="text-[11px] text-[#6B7280] mb-1">Monats-Differenz analytic − GuV (positiv = analytic höher)</div>
          <div style={{ width: '100%', height: 140 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monatsBars} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="monat" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} tickLine={false} axisLine={false}
                  tickFormatter={v => new Intl.NumberFormat('de-AT',{notation:'compact',maximumFractionDigits:1}).format(v) + ' €'} />
                <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1} />
                <Tooltip formatter={(v,_,ctx) => [fmtEurDetail(v), 'Diff']}
                  contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="diff" radius={[4,4,0,0]}>
                  {monatsBars.map((row, i) => (
                    <Cell key={i} fill={Math.abs(row.diff) < 1000 ? '#10B981' : Math.abs(row.diff) < 10000 ? '#F59E0B' : '#EF4444'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Generic Check-Card Wrapper ───────────────────────────────────────────────
function CheckCardShell({ check, summary, children, defaultOpen, badge, extra }) {
  const [open, setOpen] = useState(defaultOpen);
  const c = AMPEL_COLORS[check.ampel];
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#FAFBFC] text-left"
      >
        <span style={{ color: c.text }}>
          <c.icon width={20} height={20} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">{check.name}</h2>
            <AmpelBadge ampel={check.ampel} small />
            {badge}
          </div>
          {summary && <div className="text-xs text-[#6B7280] mt-0.5">{summary}</div>}
        </div>
        {extra}
        {open ? <ChevronDown width={16} height={16} className="text-[#9CA3AF]" /> : <ChevronRight width={16} height={16} className="text-[#9CA3AF]" />}
      </button>
      {open && <div className="border-t border-[#E5E7EB] px-4 py-3">{children}</div>}
    </div>
  );
}

// ─── Check 5 — KS-lose GuV-Konten ─────────────────────────────────────────────
function Check5Card({ check }) {
  const [openAcct, setOpenAcct] = useState(null);
  const summary = `${fmtInt(check.hits)} Buchungen · Σ ${fmtEurDetail(check.sum_balance)} · Top-${Math.min(25, check.per_acct.length)} Konten`;
  return (
    <CheckCardShell check={check} summary={summary} defaultOpen>
      <div className="text-xs text-[#6B7280] mb-2">
        Move-Lines auf Klasse 4/5/6/7 ohne analytic_distribution. Pro Anlage einmal dist setzen → propagiert auf alle AFA-Monate.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium text-[#6B7280] w-20">Code</th>
              <th className="text-left px-2 py-1.5 font-medium text-[#6B7280]">Konto</th>
              <th className="text-right px-2 py-1.5 font-medium text-[#6B7280] w-16">Lines</th>
              <th className="text-right px-2 py-1.5 font-medium text-[#6B7280] w-32">Σ balance</th>
              <th className="w-6"></th>
            </tr>
          </thead>
          <tbody>
            {check.per_acct.map(a => {
              const isOpen = openAcct === a.code;
              return (
                <>
                  <tr key={a.code} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC] cursor-pointer"
                    onClick={() => setOpenAcct(isOpen ? null : a.code)}>
                    <td className="px-2 py-1.5 tabular-nums text-[#9CA3AF]">{a.code}</td>
                    <td className="px-2 py-1.5 text-[#1A1A2E]">{a.name}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-[#4B5563]">{fmtInt(a.count)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-[#1A1A2E] font-medium">{fmtEurDetail(a.sum)}</td>
                    <td className="px-2 py-1.5 text-[#9CA3AF]">
                      {isOpen ? <ChevronDown width={12} height={12}/> : <ChevronRight width={12} height={12}/>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={a.code+'-detail'}>
                      <td colSpan={5} className="px-2 py-2 bg-[#FAFBFC]">
                        {a.spezial_hinweis && (
                          <div className="mb-2 text-[11px] bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1.5 rounded">
                            💡 {a.spezial_hinweis}
                          </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                          {a.examples.map((e, i) => (
                            <div key={i} className="text-[11px] text-[#4B5563] flex items-center justify-between gap-2 bg-white border border-[#E5E7EB] rounded px-2 py-1">
                              <span className="truncate">
                                <span className="text-[#9CA3AF]">{e.date}</span>{' '}
                                <span className="font-medium text-[#1A1A2E]">{e.move_ref}</span>
                                {e.partner && <span className="text-[#6B7280]"> · {e.partner}</span>}
                              </span>
                              <span className="tabular-nums text-[#1A1A2E] font-medium shrink-0">{fmtEurDetail(e.balance)}</span>
                            </div>
                          ))}
                          {a.count > a.examples.length && (
                            <div className="text-[10px] text-[#9CA3AF] col-span-full">…und {a.count - a.examples.length} weitere</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </CheckCardShell>
  );
}

// ─── Check 3 — Material-Bestandsveränderung ───────────────────────────────────
function Check3Card({ check }) {
  const summary = `${check.summary.rot} 🔴 · ${check.summary.amber} 🟡 · ${check.summary.gruen} ✅ · ${check.total_moves} Material-Buchungen gesamt`;
  return (
    <CheckCardShell check={check} summary={summary} defaultOpen>
      <div className="text-xs text-[#6B7280] mb-2">
        Pro Monat: existiert 5400-Aufwand? existiert 1600/1602-Bilanz-Gegenposten?
        Sitzt analytic_distribution korrekt nur auf 5400 (✅) oder auch auf der Bilanzseite (⚠️)?
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {check.monats_status.map(m => {
          const c = AMPEL_COLORS[m.ampel];
          return (
            <div key={m.monat} className="border rounded-lg p-3" style={{ borderColor: c.border, backgroundColor: c.bg }}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold" style={{ color: c.text }}>
                  {MONTH_LABELS[m.monat - 1]} 2026
                </div>
                <div className="text-[11px] font-medium" style={{ color: c.text }}>
                  {m.label}
                </div>
              </div>
              <div className="text-[11px] mt-1.5" style={{ color: c.text }}>{m.hinweis}</div>
              {m.beispiele.length > 0 && (
                <div className="mt-2 space-y-0.5">
                  {m.beispiele.map((b, i) => (
                    <div key={i} className="text-[10px] font-mono bg-white/50 rounded px-1.5 py-0.5" style={{ color: c.text }}>
                      {b.move_ref || `move #${b.move_id}`} ({b.lines} lines)
                    </div>
                  ))}
                </div>
              )}
              {m.moves > 0 && (
                <div className="text-[10px] mt-1.5 flex items-center gap-2" style={{ color: c.text, opacity: 0.7 }}>
                  <span>{m.moves} moves</span>
                  <span>·</span>
                  <span>5400: {m.hat_5400 ? '✓' : '–'} ({m.dist_on_5400 ? 'dist' : 'no dist'})</span>
                  <span>·</span>
                  <span>16xx: {m.hat_16 ? '✓' : '–'} ({m.dist_on_16 ? 'dist' : 'no dist'})</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </CheckCardShell>
  );
}

// ─── Check 2 — KS auf Bestandskonten ──────────────────────────────────────────
function Check2Card({ check, showByDesign, onToggle }) {
  const realSum = check.real_error.sum_balance;
  const byDesignSum = check.by_design.sum_balance;
  const summary = `Echte Fehler: ${fmtInt(check.real_error.hits)} Buchungen · Σ ${fmtEurDetail(realSum)}${showByDesign ? ` · Odoo-by-design: ${fmtInt(check.by_design.hits)} · Σ ${fmtEurDetail(byDesignSum)}` : ''}`;
  const toggle = (
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(!showByDesign); }}
      className={`text-[11px] px-2 py-1 rounded-md border ${showByDesign ? 'bg-[#338edc] text-white border-[#338edc]' : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:text-[#1A1A2E]'}`}
    >
      ⚙ Odoo-by-design {showByDesign ? 'AN' : 'AUS'}
    </button>
  );
  return (
    <CheckCardShell check={check} summary={summary} extra={toggle}>
      <div className="text-xs text-[#6B7280] mb-2">
        Move-Lines mit analytic_distribution auf Bestandskonten (Klasse 0/1/2/3).
        🔴 = echte Fehler (Material-Gegenposten 1600/1602 + Bilanz-Gegenkonto 2900).
        ⚙ = Odoo propagiert dist automatisch auf 3700 Verbindlichkeiten + 0660/0710/0600 AFA-Gegenposten (buchhalterisch by-design).
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium text-[#6B7280] w-8"></th>
              <th className="text-left px-2 py-1.5 font-medium text-[#6B7280] w-20">Code</th>
              <th className="text-left px-2 py-1.5 font-medium text-[#6B7280]">Konto</th>
              <th className="text-right px-2 py-1.5 font-medium text-[#6B7280] w-16">Lines</th>
              <th className="text-right px-2 py-1.5 font-medium text-[#6B7280] w-32">Σ balance</th>
            </tr>
          </thead>
          <tbody>
            {check.per_acct
              .filter(a => showByDesign || a.klassification === 'real_error')
              .map(a => (
                <tr key={a.code} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]">
                  <td className="px-2 py-1.5">
                    {a.klassification === 'real_error'
                      ? <span title="Echter Fehler">🔴</span>
                      : <span title="Odoo-by-design (Propagation)" className="text-[#9CA3AF]">⚙</span>}
                  </td>
                  <td className="px-2 py-1.5 tabular-nums text-[#9CA3AF]">{a.code}</td>
                  <td className="px-2 py-1.5 text-[#1A1A2E]">{a.name}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-[#4B5563]">{fmtInt(a.count)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-[#1A1A2E] font-medium">{fmtEurDetail(a.sum)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {showByDesign && (
        <div className="mt-2 text-[11px] bg-[#F8F9FC] border border-[#E5E7EB] text-[#6B7280] px-2 py-1.5 rounded">
          💡 {check.by_design.hinweis}
        </div>
      )}
    </CheckCardShell>
  );
}

// ─── Check 4 — Cross-KS-Lieferanten ───────────────────────────────────────────
function Check4Card({ check }) {
  const summary = `${fmtInt(check.hits)} Lieferanten mit ausreißenden KS · Σ Outlier ${fmtEurDetail(check.outlier_sum_total)} (Schwelle ≥${fmtEur(check.threshold_eur)}, Top-KS ≥${check.top_pct_min}%)`;
  return (
    <CheckCardShell check={check} summary={summary}>
      <div className="text-xs text-[#6B7280] mb-2">
        Lieferanten, die ≥{check.top_pct_min}% ihres Volumens auf einer KS haben, aber Buchungen auf einer
        anderen KS (≥{fmtEur(check.threshold_eur)}). Whitelisted: {check.whitelist.length} verwandte Paare
        (Porto↔Zoll, Spedition↔Zoll).
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium text-[#6B7280]">Lieferant</th>
              <th className="text-left px-2 py-1.5 font-medium text-[#6B7280]">Haupt-KS (%)</th>
              <th className="text-left px-2 py-1.5 font-medium text-[#6B7280]">Outlier-KS</th>
              <th className="text-right px-2 py-1.5 font-medium text-[#6B7280] w-28">Σ Outlier</th>
            </tr>
          </thead>
          <tbody>
            {check.examples.map(h => (
              <tr key={h.partner_id} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]">
                <td className="px-2 py-1.5 text-[#1A1A2E]">{h.partner_name}</td>
                <td className="px-2 py-1.5 text-[#4B5563]">
                  {h.top_ks} <span className="text-[#9CA3AF]">({h.top_pct.toFixed(1)}%)</span>
                </td>
                <td className="px-2 py-1.5 text-[#4B5563]">
                  {h.outliers.map((o, i) => (
                    <div key={i}>{o.ks}: <span className="tabular-nums">{fmtEurDetail(o.abs)}</span></div>
                  ))}
                  {h.whitelisted_outliers?.length > 0 && (
                    <div className="text-[10px] text-[#9CA3AF] mt-0.5">
                      Whitelisted: {h.whitelisted_outliers.map(w => w.ks).join(', ')}
                    </div>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-[#1A1A2E] font-medium">{fmtEurDetail(h.outlier_sum)}</td>
              </tr>
            ))}
            {check.examples.length === 0 && (
              <tr><td colSpan={4} className="px-2 py-4 text-center text-[#9CA3AF]">Keine Cross-KS-Outlier über der Schwelle.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </CheckCardShell>
  );
}

// ─── Check 7 — Durchläufer-Saldo ──────────────────────────────────────────────
function Check7Card({ check }) {
  const summary = `${check.targets.length} Durchläufer-KS · Toleranz ±${fmtEur(check.toleranz_eur)} · Max-Saldo ${fmtEurDetail(Math.max(...check.targets.map(t => t.abs_sum)))}`;
  return (
    <CheckCardShell check={check} summary={summary}>
      <div className="text-xs text-[#6B7280] mb-2">
        Durchläufer-Kostenstellen sollten Σ ≈ 0 € haben (Einnahme = weiterverrechnete Auslage).
        {check.hinweis}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {check.targets.map(t => {
          const c = AMPEL_COLORS[t.ampel];
          return (
            <div key={t.ks_id} className="border rounded-lg p-3" style={{ borderColor: c.border, backgroundColor: c.bg }}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold" style={{ color: c.text }}>
                  KS #{t.ks_id} {t.ks_name}
                </div>
                <AmpelBadge ampel={t.ampel} small label={t.ampel === 'green' ? 'in Toleranz' : 'außerhalb'} />
              </div>
              <div className="text-2xl font-bold tabular-nums mt-1" style={{ color: c.text }}>
                {fmtEurDetail(t.sum)}
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: c.text }}>
                {fmtInt(t.lines)} Buchungen · sollte ≈ 0 sein
              </div>
            </div>
          );
        })}
      </div>
    </CheckCardShell>
  );
}

// ─── Check 6 — Finecom/5720-Anker (grüne Wächter-Card) ────────────────────────
function Check6Card({ check }) {
  const summary = `Σ analytic |${fmtEurDetail(Math.abs(check.sigma_analytic))}| vs Σ move.line |${fmtEurDetail(Math.abs(check.sigma_move_line))}| · Diff ${fmtEurDetail(check.diff_abs)}`;
  return (
    <CheckCardShell check={check} summary={summary}>
      <div className="text-xs text-[#6B7280] mb-2">{check.hinweis}</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div className="border border-[#E5E7EB] rounded-lg p-3">
          <div className="text-[11px] text-[#6B7280]">Konto(en) 5720</div>
          <div className="mt-1 space-y-0.5">
            {check.accounts_5720.map(a => (
              <div key={a.id} className="text-xs">
                <span className="text-[#9CA3AF] tabular-nums">{a.code}</span> {a.name}
              </div>
            ))}
          </div>
        </div>
        <div className="border border-[#E5E7EB] rounded-lg p-3">
          <div className="text-[11px] text-[#6B7280]">Σ move.line (5720, posted)</div>
          <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums mt-0.5">{fmtEurDetail(check.sigma_move_line)}</div>
          <div className="text-[11px] text-[#9CA3AF] mt-0.5">{fmtInt(check.lines_move)} Lines</div>
        </div>
        <div className="border border-[#E5E7EB] rounded-lg p-3">
          <div className="text-[11px] text-[#6B7280]">Σ analytic.line (general=5720)</div>
          <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums mt-0.5">{fmtEurDetail(check.sigma_analytic)}</div>
          <div className="text-[11px] text-[#9CA3AF] mt-0.5">{fmtInt(check.lines_analytic)} Lines · Σ |analytic| ≡ Σ |move.line| (Vorzeichen-Konvention)</div>
        </div>
      </div>
    </CheckCardShell>
  );
}
